import React, { useState, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';

// Styles for the connectivity footer
const styles = {
  container: {
    position: 'fixed' as const,
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1050,
    maxWidth: '250px',
    width: 'auto',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  footer: {
    padding: '0.4rem 0.8rem',
    textAlign: 'center' as const,
    fontSize: '0.75rem',
    fontWeight: '500' as const,
  },
  online: {
    backgroundColor: 'var(--phoenix-success)',
    color: 'white',
  },
  offline: {
    backgroundColor: 'var(--phoenix-danger)',
    color: 'white',
  },
  icon: {
    marginRight: '0.4rem',
    fontSize: '0.7rem',
  }
};

const ConnectivityFooter: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showFooter, setShowFooter] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    // Function to handle online status change
    const handleOnline = () => {
      setIsOnline(true);
      setMessage('You are now back online');
      setShowFooter(true);
      
      // Auto-hide the footer after 5 seconds when back online
      const timer = setTimeout(() => {
        setShowFooter(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    };

    // Function to handle offline status change
    const handleOffline = () => {
      setIsOnline(false);
      setMessage('You are now offline');
      setShowFooter(true);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <CSSTransition
      in={showFooter}
      timeout={300}
      classNames="connectivity-footer"
      unmountOnExit
    >
      <div style={styles.container}>
        <div style={{...styles.footer, ...(isOnline ? styles.online : styles.offline)}}>
          <span>
            <i 
              className={`fas ${isOnline ? 'fa-wifi' : 'fa-exclamation-triangle'}`} 
              style={styles.icon}
            ></i>
            {message}
          </span>
        </div>
      </div>
    </CSSTransition>
  );
};

export default ConnectivityFooter;
